import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, User, Lock, FileText, Filter, Download } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { downloadAuthFile } from "@/lib/downloadFile";
import { relativeTime, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const EVENT_LABELS = {
  "admin.user_suspended":     "User Suspended",
  "admin.user_unsuspended":   "User Reactivated",
  "admin.user_deleted":       "User Deleted",
  "admin.user_promoted":      "Promoted to Admin",
  "admin.user_demoted":       "Demoted from Admin",
  "admin.broker_price_changed": "Broker Price Changed",
  "admin.broker_toggled":     "Broker Toggled",
  "admin.alert_confirmed":    "Alert Confirmed",
  "admin.alert_disputed":     "Alert Disputed",
  "admin.collection_alert_created": "Alert Created",
};

const eventFilters = [
  { key: "",                            label: "All events" },
  { key: "admin.user_suspended",        label: "User Suspend" },
  { key: "admin.user_deleted",          label: "User Delete" },
  { key: "admin.user_promoted",         label: "Promote Admin" },
  { key: "admin.broker_price_changed",  label: "Broker Price" },
  { key: "admin.broker_toggled",        label: "Broker Toggle" },
  { key: "admin.alert_confirmed",       label: "Alert Confirmed" },
  { key: "admin.alert_disputed",        label: "Alert Disputed" },
];

const AdminSettingsPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ full_name: "", email: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const [audit, setAudit] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditFilter, setAuditFilter] = useState("");

  useEffect(() => {
    setProfile({ full_name: user?.full_name || "", email: user?.email || "" });
  }, [user?.id]);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter) params.set("event_type", auditFilter);
      params.set("limit", "50");
      const { data } = await apiClient.get(`/admin/audit-log?${params.toString()}`);
      setAudit(data.events || []);
      setAuditTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAuditLoading(false);
    }
  };
  useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [auditFilter]);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      // Profile edit endpoint isn't part of current scope; stub with a toast so
      // the UI still feels responsive without pretending changes persist.
      await new Promise((r) => setTimeout(r, 400));
      toast.info("Profile changes ship in a future phase.");
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async () => {
    if (!oldPw || !newPw) { toast.error("Enter both fields."); return; }
    setPwSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 400));
      toast.info("Password rotation ships in a future phase.");
      setOldPw(""); setNewPw("");
    } finally {
      setPwSaving(false);
    }
  };

  const describe = (e) => {
    const meta = e.metadata || {};
    if (e.event_type === "admin.broker_price_changed") {
      const f = meta.field === "price_standard_cents" ? "Standard rate" : "PRO rate";
      const oldV = ((meta.old_value || 0) / 100).toFixed(2);
      const newV = ((meta.new_value || 0) / 100).toFixed(2);
      return `${f}: $${oldV} → $${newV}`;
    }
    if (e.event_type === "admin.broker_toggled") {
      return meta.new_value ? "Enabled" : "Disabled";
    }
    if (["admin.alert_confirmed", "admin.alert_disputed"].includes(e.event_type)) {
      const r = ((meta.reported_cents || 0) / 100).toFixed(2);
      const v = ((meta.verified_cents || 0) / 100).toFixed(2);
      return `Reported $${r} · Verified $${v}`;
    }
    return "—";
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="space-y-1">
        <div className="jea-caption">Admin</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <JeaCard data-testid="admin-profile-card">
          <div className="jea-caption mb-2">Profile</div>
          <div className="space-y-3">
            <div>
              <label className="jea-caption block mb-1" htmlFor="admin-name">Full name</label>
              <JeaInput
                id="admin-name"
                leftIcon={<User size={14} />}
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                data-testid="admin-profile-name"
              />
            </div>
            <div>
              <label className="jea-caption block mb-1" htmlFor="admin-email">Email</label>
              <JeaInput
                id="admin-email"
                leftIcon={<Mail size={14} />}
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                data-testid="admin-profile-email"
              />
            </div>
            <div className="flex justify-end">
              <JeaButton onClick={saveProfile} loading={profileSaving} data-testid="admin-profile-save">
                Save Profile
              </JeaButton>
            </div>
          </div>
        </JeaCard>

        <JeaCard data-testid="admin-password-card">
          <div className="jea-caption mb-2">Change Password</div>
          <div className="space-y-3">
            <div>
              <label className="jea-caption block mb-1" htmlFor="admin-old-pw">Current password</label>
              <JeaInput
                id="admin-old-pw"
                type="password"
                leftIcon={<Lock size={14} />}
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                data-testid="admin-old-password"
              />
            </div>
            <div>
              <label className="jea-caption block mb-1" htmlFor="admin-new-pw">New password</label>
              <JeaInput
                id="admin-new-pw"
                type="password"
                leftIcon={<Lock size={14} />}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="admin-new-password"
              />
            </div>
            <div className="flex justify-end">
              <JeaButton onClick={savePassword} loading={pwSaving} data-testid="admin-password-save">
                Update Password
              </JeaButton>
            </div>
          </div>
        </JeaCard>
      </div>

      <JeaCard data-testid="admin-audit-log">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="jea-caption">Audit Trail</div>
            <h2 className="text-white font-bold text-[18px]">
              Audit Log <span className="text-text-muted text-[13px] font-normal">({auditTotal} events)</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <select
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
                className="jea-focus h-9 pl-8 pr-4 rounded-[8px] bg-bg-tertiary border border-border-default text-white text-[13px] focus:outline-none"
                data-testid="admin-audit-filter"
              >
                {eventFilters.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={async () => {
                try { await downloadAuthFile("/admin/audit-log/export"); }
                catch (e) { toast.error(formatApiError(e, "CSV export failed.")); }
              }}
              className="h-9 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[12px] font-semibold inline-flex items-center gap-2 transition-colors"
              data-testid="admin-audit-export"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        {auditLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : audit.length === 0 ? (
          <div className="text-text-muted text-[13px] py-8 text-center">No admin actions recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="jea-caption text-text-secondary p-3 text-left">Timestamp</th>
                  <th className="jea-caption text-text-secondary p-3 text-left">Admin</th>
                  <th className="jea-caption text-text-secondary p-3 text-left">Event</th>
                  <th className="jea-caption text-text-secondary p-3 text-left">Target</th>
                  <th className="jea-caption text-text-secondary p-3 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} className="border-b border-border-default text-[13px]" data-testid={`admin-audit-row-${e.id}`}>
                    <td className="p-3 jea-date">{shortDate(e.created_at)}</td>
                    <td className="p-3 text-white">{e.admin_name || "System"}</td>
                    <td className="p-3">
                      <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-bg-tertiary border border-border-default text-text-secondary">
                        {EVENT_LABELS[e.event_type] || e.event_type}
                      </span>
                    </td>
                    <td className="p-3 text-text-secondary truncate max-w-[240px]">{e.target_label || e.target_id || "—"}</td>
                    <td className="p-3 text-text-secondary">{describe(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </JeaCard>
    </div>
  );
};

export default AdminSettingsPage;
